import { Component, Fragment, ReactNode } from "react";
import isEqual from "lodash/isEqual";
import debounce from "lodash/debounce";

import RemoteButtonComponent from "~/components/shared/RemoteButtonComponent";
import Icon from "~/components/shared/Icon";

import JobInformationStore from "~/stores/JobInformationStore";

import cable from "~/lib/cable";
import Database from "~/lib/Database";

import FriendlyTime from "~/components/shared/FriendlyTime";

interface Job {
  id: string;
  basePath: string;
}

interface Artifact {
  id: string;
  path: string;
  url: string;
  fileSize: string;
  mimeType: string;
  state: "new" | "finished" | "error" | "deleted" | "expired";
  sha256sum?: string;
  sha1sum?: string;
  createdAt: string;
  expiresAt?: string | Date;
  selfHosted?: boolean;
  canDeleteArtifact: {
    allowed: boolean;
  };
}

interface JobArtifactsProps {
  job: Job;
}

interface JobArtifactsState {
  artifacts: Artifact[] | null;
  count: number;
}

interface CableSubscription {
  unsubscribe: () => void;
}

export default class JobArtifacts extends Component<JobArtifactsProps, JobArtifactsState> {
  static displayName = "JobArtifacts";

  subscription: CableSubscription | null = null;

  constructor(props: JobArtifactsProps) {
    super(props);
    this.state = {
      artifacts: this._getFromStore(),
      count: this._getCountFromStore(),
    };
  }

  _getFromStore(): Artifact[] | null {
    return Database.parse(JobInformationStore.get(this.props.job.id, "artifacts"));
  }

  _getCountFromStore(): number {
    return Database.parse(JobInformationStore.get(this.props.job.id, "artifactsCount"));
  }

  reload = debounce(
    () => {
      return JobInformationStore.refetch(
        this.props.job.id,
        "artifacts",
        this.props.job.basePath + "/artifacts",
      );
    },
    2000,
    { leading: true, maxWait: 4000 },
  );

  _setStoreData(artifacts: Artifact[]) {
    return JobInformationStore.set(this.props.job.id, "artifacts", artifacts);
  }

  shouldComponentUpdate(nextProps: JobArtifactsProps, nextState: JobArtifactsState) {
    return !isEqual(this.props, nextProps) || !isEqual(this.state.artifacts, nextState.artifacts);
  }

  componentDidMount() {
    JobInformationStore.addChangeListener(this._onStoreChange);

    const reload = this.reload;
    this.subscription = cable.subscriptions.create(
      { channel: "Pipelines::JobChannel", uuid: this.props.job.id },
      {
        received({ event }: { event: string }) {
          if (event === "artifacts:changed") {
            reload();
          }
        },
      },
    );

    this.reload();
  }

  componentWillUnmount() {
    JobInformationStore.removeChangeListener(this._onStoreChange);

    this.subscription?.unsubscribe();
  }

  getSingleArtifactPerPath(): Artifact[] {
    const artifactsByPath = new Map<string, Artifact>();

    this.state.artifacts?.forEach((artifact) => {
      const artifactOnSamePath = artifactsByPath.get(artifact.path);

      if (
        !artifactOnSamePath ||
        new Date(artifact.createdAt) > new Date(artifactOnSamePath.createdAt)
      ) {
        artifactsByPath.set(artifact.path, artifact);
      }
    });

    return Array.from(artifactsByPath.values());
  }

  artifactListNodes(): ReactNode[] {
    const uploadedArtifacts = this.getSingleArtifactPerPath();

    return [...this.state.artifacts!]
      .sort((left, right) => left.path.localeCompare(right.path))
      .map((artifact, index, list) => {
        const checksum = artifact.sha256sum || artifact.sha1sum;
        const checksumNode = checksum && (
          <small className="text-muted min-w-0 flex-auto overflow-hidden overflow-ellipsis text-right">
            {checksum}
          </small>
        );

        const shouldLinkToDownload = uploadedArtifacts.find(({ id }) => id === artifact.id);

        const isLastItem = index === list.length - 1;
        const isTruncated = this.state.artifacts!.length < this.state.count;

        const paddingClass = index === 0 ? "pb-2" : isLastItem && !isTruncated ? "pt-2" : "py-2";

        const borderClass =
          !isLastItem || (isLastItem && isTruncated) ? " border-b border-gray" : "";

        if (artifact.state === "deleted") {
          return (
            <div key={artifact.id} className={`${paddingClass}${borderClass}`}>
              This artifact has been deleted.
            </div>
          );
        }

        return (
          <Fragment key={artifact.id}>
            <div
              className={`flex items-start px-1 ${paddingClass}${borderClass} ${artifact.state}`}
            >
              <div className="mr-3 flex-none items-center">{this.renderArtifactIcon(artifact)}</div>
              <div className="flex min-w-0 flex-auto flex-col">
                <div className="flex min-w-0 flex-auto flex-wrap items-center">
                  {this.renderArtifactLink(artifact, !!shouldLinkToDownload)}
                  <small className="text-muted">{artifact.fileSize}</small>
                </div>
                <div className="flex min-w-0 flex-auto flex-wrap items-center">
                  <small className="text-muted">{artifact.mimeType}</small>
                  {checksumNode}
                </div>
              </div>
              <div className="ml-3 flex-none items-center">{this.renderDeleteButton(artifact)}</div>
            </div>
            {isLastItem && isTruncated ? this.renderTruncatedMessage() : null}
          </Fragment>
        );
      });
  }

  renderTruncatedMessage(): ReactNode {
    return (
      <div className="px-1 pt-4">
        <strong>Need more than 1,000? </strong>
        To show all {this.state.count} artifacts, please use{" "}
        <a
          href="/docs/apis/rest-api/artifacts"
          className="lime hover-lime hover-underline no-underline"
          target="_blank"
        >
          our API
        </a>
      </div>
    );
  }

  renderArtifactIcon(artifact: Artifact): ReactNode {
    if (artifact.state === "finished") {
      return <Icon icon="heroicons/outline/document" className="h-4 w-4" />;
    } else if (artifact.state === "error") {
      return <Icon icon="heroicons/outline/document" className="red h-4 w-4" />;
    }

    return <Icon icon="heroicons/outline/document" className="dark-grey h-4 w-4" />;
  }

  renderArtifactLink(artifact: Artifact, shouldLinkToDownload: boolean): ReactNode {
    if (artifact.state === "finished" && shouldLinkToDownload) {
      return (
        <a
          href={artifact.url}
          title={artifact.path}
          className="flex min-w-0 flex-auto items-center overflow-hidden overflow-ellipsis"
          target="_blank"
          rel="noopener noreferrer"
        >
          {artifact.path}
        </a>
      );
    } else if (artifact.state === "finished") {
      return (
        <span className="dark-gray flex min-w-0 flex-auto items-center overflow-hidden overflow-ellipsis">
          {`${artifact.path} (outdated)`}
        </span>
      );
    } else if (artifact.state === "error") {
      return (
        <span className="red flex min-w-0 flex-auto items-center overflow-hidden overflow-ellipsis">
          {`${artifact.path} (failed to upload, see log for details)`}
        </span>
      );
    } else if (artifact.state === "new") {
      return (
        <span className="dark-gray flex min-w-0 flex-auto items-center overflow-hidden overflow-ellipsis">
          {`${artifact.path} (uploading…)`}
        </span>
      );
    } else if (artifact.state === "expired") {
      return (
        <span className="dark-gray flex min-w-0 flex-auto items-center overflow-hidden overflow-ellipsis">
          {artifact.path} (expired&nbsp;{" "}
          <FriendlyTime value={artifact.expiresAt!} capitalized={false} />)
        </span>
      );
    }
  }

  renderDeleteButton(artifact: Artifact): ReactNode {
    if (artifact.state === "finished" && artifact.canDeleteArtifact.allowed) {
      const confirmText = artifact.selfHosted
        ? "As this file is not hosted by Buildkite, deleting it will remove the record from our database, but the file will remain where it was uploaded to. Are you sure you want to delete it?"
        : "Deleting this artifact will remove it from Buildkite and the file will be permanently deleted from our systems. This action cannot be undone. Are you sure you want to delete it?";

      return (
        <RemoteButtonComponent
          url={this.props.job.basePath + `/artifacts/${artifact.id}`}
          method="delete"
          confirmText={confirmText}
          onSuccess={this.handleArtifactDeleteSuccess}
          tooltipText="Delete"
          className="dark-gray text-center"
        >
          <Icon icon="heroicons/outline/trash" className="hover-black h-4 w-4" />
        </RemoteButtonComponent>
      );
    }
  }

  handleArtifactDeleteSuccess = (_event: Event, data: string) => {
    return this._setStoreData(JSON.parse(data));
  };

  render() {
    return (
      <div data-testid="JobArtifactsComponent" className="artifacts-list mt-[10px] pb-2">
        {this.state.artifacts && this.state.artifacts.length ? (
          <div className="artifacts-table">{this.artifactListNodes()}</div>
        ) : this.state.artifacts && this.state.artifacts.length === 0 ? (
          <p className="m-0">There are no artifacts for this job.</p>
        ) : (
          <span>Loading…</span>
        )}
      </div>
    );
  }

  _onStoreChange = () => {
    return this.setState({
      artifacts: this._getFromStore(),
      count: this._getCountFromStore(),
    });
  };
}
